import type { HTMLAttributes } from "react";
import { formatMoney, formatMoneyCompact } from "@finances/shared/money";
import styles from "./Money.module.css";
import { cx } from "./cx.js";

interface MoneyProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Integer pence, signed. Negative is money out. */
  pence: number;
  /**
   * How to colour it.
   * - `flow` (default): money in is green, money out is plain text.
   * - `balance`: only a genuinely negative balance — an overdraft or a debt —
   *   is coloured, because a large outgoing is not a failure.
   * - `none`: inherit.
   */
  tone?: "flow" | "balance" | "none";
  /** Abbreviate to £12.4k. Summary tiles only — never a table, never a field. */
  compact?: boolean;
  /** Show a leading + on positives. */
  signed?: boolean;
  decimals?: 0 | 2;
  /** Right-align. On by default, because most figures live in a column. */
  align?: boolean;
  size?: "sm" | "base" | "lg" | "xl" | "2xl" | "3xl";
  weight?: "normal" | "medium" | "semibold";
}

/**
 * Every figure in the app goes through here.
 *
 * Colour is never the only carrier of meaning: the sign, the position and the
 * surrounding label say the same thing, because roughly one in twelve men has
 * a red/green deficiency and finance UIs are the classic offender.
 */
export function Money({
  pence,
  tone = "flow",
  compact = false,
  signed = false,
  decimals = 2,
  align = true,
  size = "base",
  weight = "normal",
  className,
  ...rest
}: MoneyProps) {
  const text = compact ? formatMoneyCompact(pence) : formatMoney(pence, { signed, decimals });

  const toneClass =
    tone === "none"
      ? undefined
      : tone === "balance"
        ? pence < 0 && styles.negative
        : pence > 0
          ? styles.in
          : styles.out;

  return (
    <span
      className={cx(
        styles.money,
        align && styles.aligned,
        styles[`size-${size}`],
        styles[`weight-${weight}`],
        toneClass,
        className,
      )}
      // The compact form is an abbreviation; the exact figure stays reachable.
      title={compact ? formatMoney(pence) : undefined}
      {...rest}
    >
      {text}
    </span>
  );
}
